import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';

const steps = [
  'Search by composer, piece, or keyword.',
  'Preview the score, hear the MIDI, and follow along instantly.',
  'Practice with synced violin tabs and measure guides.'
];

const highlights = [
  'Curated violin repertoire',
  'High-resolution score viewer',
  'Automatic tab extraction'
];

const HomePage = ({ setSelectedPiece, results, favorites = [], addFavorite, removeFavorite, isFavorited }) => {
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState({ composer: '', difficulty: '', instrument: '' });

  const handlePieceClick = (piece) => {
    setSelectedPiece(piece);
    navigate('/piece');
  };

  const handlePieceKeyDown = (event, piece) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePieceClick(piece);
    }
  };

  // Get unique values for filters
  const composers = [...new Set(results.map(p => p.composer).filter(Boolean))].sort();
  const difficulties = [...new Set(results.map(p => p.difficulty).filter(Boolean))].sort();
  const instruments = [...new Set(results.flatMap(p => p.instruments).filter(Boolean))].sort();

  // Filter results
  const filteredResults = results.filter(piece => {
    if (filters.composer && piece.composer !== filters.composer) return false;
    if (filters.difficulty && piece.difficulty !== filters.difficulty) return false;
    if (filters.instrument && !piece.instruments.includes(filters.instrument)) return false;
    return true;
  });

  const totalResults = filteredResults.length;
  const hasResults = totalResults > 0;

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setFilters({ composer: '', difficulty: '', instrument: '' });
  };

  return (
    <div className="home-page">
      {!results.length && (
        <section className="hero-card">
          <p className="hero-eyebrow">Meet Studyvarious</p>
          <h1 className="hero-title">Your violin AI teacher — friendly, focused, and ready to play.</h1>
          <p className="hero-subtitle">
            Studyvarious combines a searchable score library with an AI-powered violin teacher.
            Ask natural-language questions, get fingering suggestions, practice plans, and instant
            synced tab & score views that follow playback.
          </p>

          <div className="hero-features-row">
            <div className="hero-feature-card">
              <h3 className="hero-feature-title">The Violin AI Teacher</h3>
              <ul className="hero-feature-list">
                <li><strong>Real-time fingering suggestions</strong> — optimized for comfort and intonation.</li>
                <li><strong>Measure-level feedback</strong> — see where timing or intonation slips occur.</li>
                <li><strong>Personalized practice plans</strong> — short focused exercises to improve fast.</li>
                <li><strong>Ask in plain English</strong> — "How to practice the double-stops in bar 12?"</li>
              </ul>
            </div>

            <div className="hero-feature-card">
              <h3 className="hero-feature-title">What the app can do</h3>
              <ul className="hero-feature-list">
                <li><strong>Search pieces</strong> — by composer, movement, technique, or keywords.</li>
                <li><strong>Score & tab view</strong> — side-by-side, measure-synced, and printable.</li>
                <li><strong>Play-along MIDI</strong> — adjustable tempo, loop measures, and follow cursor.</li>
                <li><strong>Tuner & practice modes</strong> — keep intonation and rhythm on point.</li>
                <li><strong>Favorites & playlists</strong> — save pieces and practice sessions.</li>
              </ul>
            </div>
          </div>

          <div className="hero-cta-row">
            <button className="primary-cta" onClick={() => navigate('/practice')}>Try a guided practice</button>
            <button className="secondary-cta" onClick={() => navigate('/')}>Search pieces</button>
          </div>
        </section>
      )}

      <section className={`results-section ${results.length > 0 ? 'results-section--focused' : ''}`}>
        <div className="results-header">
          <div>
            <h2 className="results-title">{filteredResults.length > 0 ? 'Pieces ready to explore' : 'No pieces match your filters'}</h2>
            <p className="results-subtitle">
              {filteredResults.length > 0
                ? 'Tap a card to open the score, listen to the MIDI, and follow along with tabs.'
                : 'Try adjusting your filters or clearing them to see more results.'}
            </p>
          </div>
          <span className="results-count">{totalResults} result{totalResults === 1 ? '' : 's'}</span>
        </div>

        {results.length > 0 && (
          <div className="filters-section">
            <div className="filters">
              <select
                value={filters.composer}
                onChange={(e) => handleFilterChange('composer', e.target.value)}
                className="filter-select"
              >
                <option value="">Composer</option>
                {composers.map(composer => (
                  <option key={composer} value={composer}>{composer}</option>
                ))}
              </select>
              <select
                value={filters.difficulty}
                onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                className="filter-select"
              >
                <option value="">Difficulty</option>
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
              <select
                value={filters.instrument}
                onChange={(e) => handleFilterChange('instrument', e.target.value)}
                className="filter-select"
              >
                <option value="">Instrument</option>
                {instruments.map(instrument => (
                  <option key={instrument} value={instrument}>{instrument}</option>
                ))}
              </select>
              {(filters.composer || filters.difficulty || filters.instrument) && (
                <button onClick={clearFilters} className="clear-filters-btn">
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {filteredResults.length > 0 ? (
          <div className="pieces-container">
              {filteredResults.map((piece, index) => {
              const composerName = piece.composer || 'Unknown composer';
              const difficulty = piece.difficulty && piece.difficulty !== 'Unknown' ? piece.difficulty : null;
              const instrument = piece.instruments && piece.instruments.length > 0 && !piece.instruments.includes('Unknown') ? piece.instruments.join(', ') : null;

              return (
                <div
                  key={`${piece.id || piece.title}-${index}`}
                  className="piece-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePieceClick(piece)}
                  onKeyDown={(event) => handlePieceKeyDown(event, piece)}
                >
                  <button
                    className={`favorite-button ${isFavorited && isFavorited(piece) ? 'favorited' : ''}`}
                    aria-label={isFavorited && isFavorited(piece) ? 'Remove favorite' : 'Add favorite'}
                    onClick={(e) => { e.stopPropagation(); (isFavorited && isFavorited(piece)) ? removeFavorite(piece) : addFavorite(piece); }}
                  >
                    <FontAwesomeIcon icon={faHeartSolid} />
                  </button>
                  <div className="piece-box__header">
                    <span className="piece-box__badge">{composerName}</span>
                    {difficulty && <span className="piece-box__badge piece-box__badge--difficulty">{difficulty}</span>}
                  </div>
                  <h3 className="piece-box__title">{piece.title.replace(/\.(musicxml|mxl|xml)$/i, '').replace(/_/g, ' ')}</h3>
                  <p className="piece-box__meta">
                    {instrument || 'Unknown instrument'}
                  </p>
                  <span className="piece-box__cta">Open piece &gt;</span>
                </div>
              );
            })}
          </div>
        ) : results.length > 0 ? (
          <div className="empty-state">
            <h3 className="empty-title">No pieces match your filters</h3>
            <p className="empty-description">
              Try selecting different filter options or clear all filters to see more results.
            </p>
            <button onClick={clearFilters} className="clear-filters-btn">Clear Filters</button>
          </div>
        ) : (
          <div className="empty-state">
            <h3 className="empty-title">Ready when you are</h3>
            <p className="empty-description">
              Try a composer such as Bach, Paganini, or Ysae, or search by techniques like double stops or chaconne.
            </p>
            <ul className="empty-suggestions">
              <li>Search by movement: "Partita", "Adagio", "Allemande"</li>
              <li>Look for solo or accompaniment combinations</li>
              <li>Filter by difficulty with terms like "advanced" or "study"</li>
            </ul>
          </div>
        )}
      </section>
    </div>
  );
};

export default HomePage;
